import { applicationPath } from '@vivaporta/work-areas';
import type { LandingLocale } from './landingContentTypes.js';
import type { StaticPageContent, StaticPageAudience } from './staticPageContent.js';
import { resolveApplicationPortalBase } from './landingContentShared.js';
import { retailIndustryPageKeys, retailIndustryLabels, type RetailIndustryPageKey } from './retailIndustryLabels.js';
import bakery from '../../img/globus-jobs/globus-bakery.webp';
import bakeryWork from '../../img/globus-campaign/bäckerei6.webp';
import butcher from '../../img/globus-jobs/globus-butcher.webp';
import butcherWork from '../../img/globus-campaign/metzger1.webp';
import counter from '../../img/globus-jobs/globus-fresh-counter.webp';
import retail from '../../img/industries/retail-portrait.webp';
import kitchen from '../../img/globus-jobs/globus-gastronomy.webp';
import service from '../../img/globus-campaign/Gastro.webp';
const photos = {
  gastronomie: [kitchen, service],
  baecker: [bakery, bakeryWork],
  metzger: [butcher, butcherWork],
  frischetheke: [counter, retail],
};
const content = {
  de: {
    gastronomie: [
      'Gastronomie. Dein neuer Start.',
      'Start',
      'Dein Können zählt in Küche und Service. Wir begleiten deine Bewerbung und den passenden Einstieg in ein Team in Deutschland.',
      'Ihr Gastroteam braucht Können.',
      'Können',
      'Wir unterstützen Ihr Unternehmen bei der Suche nach passenden internationalen Mitarbeitenden für Küche und Service.',
      ['Küche', 'Service', 'Gastfreundschaft'],
    ],
    baecker: [
      'Backhandwerk mit Zukunft.',
      'Zukunft',
      'Du backst mit Leidenschaft oder berätst gern im Verkauf? Wir begleiten deinen Einstieg in die Produktion und den Verkauf frischer Backwaren.',
      'Ihre Bäckerei braucht Können.',
      'Können',
      'Wir unterstützen Ihre Bäckerei bei der Suche nach internationalen Mitarbeitenden für Produktion und Verkauf.',
      ['Produktion', 'Backwaren', 'Verkauf'],
    ],
    metzger: [
      'Metzgerhandwerk mit Zukunft.',
      'Zukunft',
      'Von der Verarbeitung bis zum Fachverkauf: Wir begleiten deine Bewerbung und klären gemeinsam, welche Aufgaben zu deiner Erfahrung passen.',
      'Ihr Betrieb braucht Fleischprofis.',
      'Fleischprofis',
      'Wir helfen Ihrem Unternehmen, internationale Mitarbeitende für Fleischverarbeitung, Produktion und Fachverkauf zu finden.',
      ['Produktion', 'Verarbeitung', 'Fachverkauf'],
    ],
    frischetheke: [
      'Frischetheke. Nah am Menschen.',
      'Menschen',
      'Käse, Fisch oder Fleisch: Zeige dein Produktwissen und deine Freude am Kundenkontakt. Wir begleiten deinen Einstieg an der Frischetheke.',
      'Ihre Frischetheke braucht Profis.',
      'Profis',
      'Wir bringen Ihr Unternehmen mit internationalen Mitarbeitenden für Beratung und Verkauf an Käse-, Fisch- und Fleischtheken zusammen.',
      ['Käse', 'Fisch', 'Fleisch'],
    ],
  },
  en: {
    gastronomie: [
      'Gastronomy. Your next step.',
      'step',
      'Bring your skills to the kitchen or service team. We guide your application and help prepare your start with an employer in Germany.',
      'Your kitchen needs a team.',
      'team',
      'We help your business find international staff for kitchen and service roles.',
      ['Kitchen', 'Service', 'Hospitality'],
    ],
    baecker: [
      'Baking with a future.',
      'future',
      'Enjoy baking or advising customers? We support your application for the production and sale of fresh bakery goods in Germany.',
      'Your bakery needs skills.',
      'skills',
      'We help your bakery find international staff for production and sales.',
      ['Production', 'Bakery goods', 'Sales'],
    ],
    metzger: [
      'Butchery with a future.',
      'future',
      'From processing to specialist sales, we guide your application and discuss which responsibilities fit your experience.',
      'Your business needs butchers.',
      'butchers',
      'We support your search for international staff in meat processing, production and specialist sales.',
      ['Production', 'Processing', 'Specialist sales'],
    ],
    frischetheke: [
      'Fresh counters. Real people.',
      'people',
      'Cheese, fish or meat: show your product knowledge and customer service skills. We guide your start at a fresh food counter.',
      'Your fresh counter needs skills.',
      'skills',
      'We help your business find international staff for advice and sales at cheese, fish and meat counters.',
      ['Cheese', 'Fish', 'Meat'],
    ],
  },
  tr: {
    gastronomie: [
      'Gastronomide geleceğin.',
      'geleceğin',
      'Mutfakta veya serviste becerilerini göster. Başvuruna ve Almanya’da sana uygun bir ekibe katılmana destek oluyoruz.',
      'Gastronomi ekibiniz güçlensin.',
      'güçlensin',
      'Mutfak ve servis için işletmenize uygun uluslararası çalışanları bulmanıza yardımcı oluyoruz.',
      ['Mutfak', 'Servis', 'Misafirperverlik'],
    ],
    baecker: [
      'Fırıncılıkta geleceğin.',
      'geleceğin',
      'Üretimde çalışmayı veya müşterilere yardımcı olmayı seviyor musun? Almanya’da taze unlu mamullerin üretimi ve satışına başvurunda yanındayız.',
      'Fırınınıza güçlü bir ekip.',
      'ekip',
      'Fırınınızın üretim ve satış bölümleri için uluslararası çalışanlar bulmanıza destek oluyoruz.',
      ['Üretim', 'Unlu mamuller', 'Satış'],
    ],
    metzger: [
      'Kasaplıkta geleceğin.',
      'geleceğin',
      'Et işlemeden uzman satışa kadar deneyimine uygun görevleri birlikte değerlendiriyor, Almanya’daki başvuruna destek oluyoruz.',
      'Kasap ekibiniz güçlensin.',
      'güçlensin',
      'Et işleme, üretim ve uzman satış için işletmenize uygun uluslararası çalışanlar bulmanıza yardımcı oluyoruz.',
      ['Üretim', 'İşleme', 'Uzman satış'],
    ],
    frischetheke: [
      'Taze ürünler. Güçlü iletişim.',
      'iletişim',
      'Peynir, balık veya et: ürün bilgini ve müşterilerle iletişim becerilerini göster. Almanya’da taze ürün tezgâhında işe başlamana destek oluyoruz.',
      'Taze ürün ekibiniz güçlensin.',
      'güçlensin',
      'Peynir, balık ve et reyonlarınızda danışmanlık ve satış için uluslararası çalışanlar bulmanıza destek oluyoruz.',
      ['Peynir', 'Balık', 'Et'],
    ],
  },
} as const;
const ui = {
  de: {
    apply: 'Hier anmelden',
    contact: 'Fachkräfte anfragen',
    overview: 'Deine Arbeitsbereiche',
    prepare: 'Dein nächster Schritt',
    prepareText:
      'Erzähle uns von deiner Ausbildung und Berufserfahrung. Gemeinsam besprechen wir passende Aufgaben, Sprache und die Vorbereitung auf deinen Start in Deutschland.',
    cta: 'Starte deine Bewerbung',
    text: 'Wähle deinen Bereich und fülle den passenden Anmeldebogen aus. Du kannst weitere Arbeitsbereiche ergänzen.',
    process: 'So funktioniert es',
  },
  en: {
    apply: 'Apply now',
    contact: 'Request specialists',
    overview: 'Your work areas',
    prepare: 'Your next step',
    prepareText:
      'Tell us about your training and experience. Together, we discuss suitable roles, language preparation and your start in Germany.',
    cta: 'Start your application',
    text: 'Choose your area and complete the matching application form. You can add further work areas.',
    process: 'How it works',
  },
  tr: {
    apply: 'Hemen başvur',
    contact: 'İletişime geç',
    overview: 'Çalışma alanların',
    prepare: 'Bir sonraki adımın',
    prepareText:
      'Eğitimini ve iş deneyimini bize anlat. Uygun görevleri, dil hazırlığını ve Almanya’daki başlangıcını birlikte planlayalım.',
    cta: 'Başvurunu başlat',
    text: 'Alanını seç ve uygun başvuru formunu doldur. Başka çalışma alanları da ekleyebilirsin.',
    process: 'Nasıl çalışır',
  },
};
export const retailIndustryHeroCopy = (
  key: RetailIndustryPageKey,
  locale: LandingLocale,
  audience: StaticPageAudience,
) => {
  const c = content[locale][key];
  return {
    title: c[audience === 'company' ? 3 : 0],
    heroTitleHighlight: c[audience === 'company' ? 4 : 1],
    intro: c[audience === 'company' ? 5 : 2],
  };
};
export const createRetailIndustryPages = (
  locale: LandingLocale = 'de',
): Record<RetailIndustryPageKey, StaticPageContent> =>
  Object.fromEntries(
    retailIndustryPageKeys.map(key => {
      const c = content[locale][key],
        words = retailIndustryLabels[locale][key],
        u = ui[locale],
        action = { label: u.apply, href: `${resolveApplicationPortalBase()}${applicationPath(key, locale)}?lang=${locale}` };
      const gallery = photos[key].map((image, index) => ({
        image,
        imageAlt: `${words[0]} – ${c[6][index]}`,
        title: c[6][index],
      }));
      return [
        key,
        {
          key,
          eyebrow: words[0],
          ...retailIndustryHeroCopy(key, locale, 'applicant'),
          heroBackgroundWord: words[0].split(/[–:]/u)[0]!.trim(),
          heroImage: photos[key][0],
          heroGallery: gallery,
          heroAction: action,
          heroHighlights: [...c[6]],
          cardsTitle: u.overview,
          cards: c[6].map(title => ({
            title,
            text: words[1],
            iconClass: 'icon-[material-symbols--check-circle-outline-rounded]',
          })),
          sections: [
            {
              eyebrow: words[0],
              title: u.prepare,
              text: u.prepareText,
              image: photos[key][1],
              imageAlt: gallery[1]!.imageAlt,
            },
          ],
          cta: {
            eyebrow: words[0],
            title: u.cta,
            text: u.text,
            primaryAction: action,
            secondaryAction: { label: u.process, href: '/ablauf' },
          },
        },
      ];
    }),
  ) as Record<RetailIndustryPageKey, StaticPageContent>;
